import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDogDayManager } from "@/hooks/useDogDayManager";
import { useDogList } from "@/context/DogListContext";
import { useConfig } from "@/context/ConfigContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { useUser } from "@/context/UserContext";
import { getFormattedDateHeader, isValidDate } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

 import { PageHeaderWrapper } from "@/components/PageHeaderWrapper";
 import { DateDisplay } from "@/components/DateDisplay";
 import { ControlsSectionWrapper } from "@/components/ControlsSectionWrapper";
 import { DogManagementSectionWrapper } from "@/components/DogManagementSectionWrapper";
 import { AddDogDialogWrapper } from "@/components/AddDogDialogWrapper";
 import { PdfUploadDialog } from "@/components/PdfUploadDialog";
 import { ProfileActionBar } from "@/components/ProfileActionBar";
 import { AlertModal } from "@/components/AlertModal";
  import { AlertBanner } from "@/components/AlertBanner";
  import { PlanUpgradeModal } from "@/components/PlanUpgradeModal";
  import { WelcomeModal } from "@/components/WelcomeModal";
   import { FreeBanner } from "@/components/FreeBanner";
   import DebugBar from "@/components/DebugBar";

import { subscribeDogs, saveDogs, subscribeStates, saveStates, subscribeRouteStatus, saveRouteStatus } from "@/realtimeDogs";
import { getWeekDays, formatDateKey, addWeeksToDateKey, parseDateKey, makeDayKey } from "@/lib/dateUtils";
import { getDogsFor } from "@/lib/dogStateHelpers";
import { makeDogStateKey, getDogIdentity } from "@/lib/dogIdentity";

import { extendTemplateWindow, hydrateRecurringNotesForExistingDay } from "@/lib/templateSync";
import { auth, db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import { ENABLE_AUTO_SAVE_DOGS, LOCAL_ONLY_MODE } from "../config/featureFlags";
import { createWorkspaceIfMissing } from "@/lib/workspaceUtils";
import { makeEditLockKey } from "@/lib/editLockKey";
import { enqueueDocWrite } from "@/lib/docWriteQueue";
import { loadLocalDaysList, saveLocalDaysList } from "../localStorageDogs";

import { saveAlert, sendAlertReply } from "@/lib/alertsSync";
import { createAlertPushEvent } from "@/services/pushEvents";
import { getOrCreateDeviceId, initializePushNotifications } from "@/services/pushNotifications";
import { useAlerts } from "@/hooks/useAlerts";
import { useToast } from "@/components/ui/use-toast";
import { loadFromStorage, saveToStorage } from "@/lib/storage";
import { LOCAL_STORAGE_KEYS } from "@/config/constants";

console.log("[AUTH_IMPORT_TEST]", !!auth, auth?.constructor?.name);

// Convierte la lista local a objetos normalizados
function localDogObjects(list) {
  return (list || []).map((dog) => {
    if (typeof dog === "string") {
      return {
        name: dog.trim(),
        originalOrder: 0,
        count: 1,
        isFixed: true,
        whatsappGroup: null,
        firstAddedDateKey: null,
        informativeNote: "",
        schedules: []
      };
    }
    return {
      name: dog.name || "",
      ...(dog.id ? { id: String(dog.id) } : {}),
      ...(dog.importKey ? { importKey: String(dog.importKey) } : {}),
      originalOrder: dog.originalOrder ?? 0,
      count: dog.count ?? 1,
      isFixed: dog.isFixed ?? true,
      whatsappGroup: dog.whatsappGroup || null,
      locationUrl: dog.locationUrl || null,
      firstAddedDateKey: dog.firstAddedDateKey || null,
      informativeNote: dog.informativeNote || "",
      schedules: Array.isArray(dog.schedules) ? dog.schedules : [],
      recurringNotes: Array.isArray(dog.recurringNotes) ? dog.recurringNotes : [],
      informativeNoteOverride: typeof dog.informativeNoteOverride === "boolean"
        ? dog.informativeNoteOverride
        : false,
    };

  }).filter(d => d.name);
}

// Variable global para desconectar Firestore antes del reset
let disconnectFirestoreFunction = null;

export function getDisconnectFirestore() {
  return disconnectFirestoreFunction;
}

// Función auxiliar para generar key compuesta perfil::dayKey
const dogKey = (profile, dayKey) => `${profile}::${dayKey}`;

// Matcher exclusivo para localizar, dentro de un array de perros (local o
// remoto RAW), el registro correspondiente a un lock de edición pendiente.
// Mismo criterio documentado en el handoff: id si ambos lo tienen, si no
// fallback por oldName/newName (legacy). No sustituye a matchesEditedDog
// (que vive en dogDayManagerActions.js y solo se usa allí).
function matchesLockDogEntry(d, lock) {
  if (!d || !lock || !lock.dogMatch) return false;
  const { id, oldName, newName } = lock.dogMatch;
  if (id && d.id) return d.id === id;
  return d.name === oldName || d.name === newName;
}

// Comparador semántico: ignora orden de claves en objetos, conserva orden en arrays
function areSyncPayloadsEqual(a, b) {
  // Tipos: null, undefined, primitivos
  if (a === null && b === null) return true;
  if (a === undefined && b === undefined) return true;
  if (a === null || b === null) return false;
  if (a === undefined || b === undefined) return false;

  const typeA = typeof a;
  const typeB = typeof b;
  if (typeA !== typeB) return false;

  // Primitivos
  if (typeA !== 'object') return a === b;

  // Arrays: orden SÍ importa
  const isArrayA = Array.isArray(a);
  const isArrayB = Array.isArray(b);
  if (isArrayA !== isArrayB) return false;

  if (isArrayA) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!areSyncPayloadsEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Objetos: orden NO importa
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();

  if (JSON.stringify(keysA) !== JSON.stringify(keysB)) return false;

  for (const k of keysA) {
    if (!areSyncPayloadsEqual(a[k], b[k])) return false;
  }
  return true;
}

export function AppCore() {
  // Para evitar bucles y carreras
  const profilePinDialogOpenerRef = useRef(null);
  const applyingRemote = useRef(false);
  const hydrated = useRef(false);
  const saveTimer = useRef(null);
  const weekSubscriptions = useRef([]);
  const stateSubscriptionsRef = useRef({});
  const routeStatusSubscriptionsRef = useRef({});
  const statesApplyingRemote = useRef(false);
  const routeStatusApplyingRemote = useRef(false);
  const routeStatusHydrated = useRef(false);
  const lastSavedDaysListRef = useRef({});
  // --- Coordinación edición/autoguardado/snapshots (weekOfChange) ---
  // editLocksRef: Map<lockKey, lockObject> — SIEMPRE un Map, en todos los
  // accesos (AppCore y dogDayManagerActions comparten esta misma instancia
  // vía useDogDayManager).
  const editLocksRef = useRef(new Map());
  // editRevisionCounterRef: contador monótono para generar myEditRevision
  // única por operación de edición (handleEditDogInList).
  const editRevisionCounterRef = useRef(0);
  // daysListRef: SIEMPRE la versión más reciente de daysList, sincronizada
  // en el useEffect de abajo y también actualizada explícitamente dentro
  // de cualquier setDaysList relacionado con el manejo de locks.
  const daysListRef = useRef({});
  const extendingTemplateRef = useRef(false); // Lock para template extension

   const lastLocalStateChangeRef = useRef(0);
   const pushInitializedRef = useRef(false); // Guard: init push solo una vez por sesion
   const isClearingProfileRef = useRef(false); // Guard: impide doble-ejecución del borrado
   const adminWorkspaceIdRef = useRef(null); // ✅ NUEVO: Almacenar adminUid resuelto para routeStatus (worker)
   
   // ✅ NUEVO: Track composite keys modificados por el usuario (dirtyKeysRef)
   // Permite distinguir entre placeholder (no guardar) y real vacío (guardar)
   const dirtyKeysRef = useRef(new Set());
  
   const markDirty = (profile, dayKey) => {
     const compositeKey = `${profile}::${dayKey}`;
     dirtyKeysRef.current.add(compositeKey);
   };
  
   const clearDirty = (profile, dayKey) => {
     const compositeKey = `${profile}::${dayKey}`;
     dirtyKeysRef.current.delete(compositeKey);
   };

  // ✅ NUEVO: Track EXPLICIT EMPTY deletes (usuario borró todos los perros deliberadamente)
  // SEPARADO de dirtyKeysRef para mayor precisión
  const deletedAllKeysRef = useRef(new Set());
  
   const markDeletedAll = (profile, dayKey) => {
     const compositeKey = `${profile}::${dayKey}`;
     deletedAllKeysRef.current.add(compositeKey);
   };
  
   const clearDeletedAll = (profile, dayKey) => {
     const compositeKey = `${profile}::${dayKey}`;
     deletedAllKeysRef.current.delete(compositeKey);
   };
  
  // ✅ Pasar refs a useDogDayManager para persistencia inmediata de add dog y manejo de deletes
  // + refs de coordinación edición/autoguardado/snapshots (weekOfChange).
  const manager = useDogDayManager(
    lastSavedDaysListRef,
    markDirty,
    markDeletedAll,
    clearDeletedAll,
    editLocksRef,
    editRevisionCounterRef,
    daysListRef
  );

  // daysListRef SIEMPRE refleja la versión más reciente de daysList. Esta
  // sincronización pasiva cubre todos los caminos que esta solución NO
  // toca (add/rename/reorder/delete/snapshot sin lock activo). Los
  // caminos CON lock activo (autoguardado y handleDaySyncFromCloud más
  // abajo) también asignan daysListRef.current explícitamente dentro de
  // su propio setDaysList, de forma síncrona con el nextDaysList devuelto
  // a React.
  useEffect(() => {
    daysListRef.current = manager.daysList;
  }, [manager.daysList]);

     const { setDaysList, setCompletedDogs, setUrgentDogs } = useDogList();
     const { config, userRole, workspaceId, assignedProfileIds } = useConfig();
     const { permissions } = useSubscription();
  const { selectedUserName, isProfileLocked, lockProfile, trustedDeviceChecking } = useUser();
   const [isPdfUploadOpen, setIsPdfUploadOpen] = useState(false);
   const [isPlanUpgradeOpen, setIsPlanUpgradeOpen] = useState(false);
   const [routeStatus, setRouteStatus] = useState({});
   const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
   const [isSubmittingAlert, setIsSubmittingAlert] = useState(false);
   // 'new' = crear aviso nuevo (broadcast vía saveAlert); 'reply' = responder al
   // aviso actualmente mostrado (dirigido vía sendAlertReply). Determina la
   // rama que ejecuta handleSubmitAlert al enviar el modal.
   const [alertModalMode, setAlertModalMode] = useState('new');
   const [reorderUnlocked, setReorderUnlocked] = useState(false);
   const { toast } = useToast();

    // Resolución del profileId CANÓNICO real del perfil activo, reutilizando
    // config.profiles (fuente ya existente, ninguna fuente nueva). manager.
    // selectedUser puede contener el id o el name del perfil según el flujo;
    // aquí se busca el perfil correspondiente por ambos criterios y se toma
    // su id real (UUID de accountMemberships/config.profiles) como
    // profileId canónico para el dismiss del aviso original. Si no se
    // encuentra el perfil (caso residual), se cae a manager.selectedUser
    // como fallback, igual que el comportamiento previo.
    const activeProfileForAlerts = config.profiles?.find(
      p => String(p.id ?? p.name) === String(manager.selectedUser)
    );
    const activeCanonicalProfileId = activeProfileForAlerts?.id ?? manager.selectedUser;

    // ✅ NUEVO: Resolución de identidad canónica para avisos
    // Para Worker: anclada a assignedProfileIds[0] (no al perfil visualmente seleccionado)
    // Para Admin/Admin2: usa el perfil activo (comportamiento actual)
    const alertsProfileForWorker =
      userRole === 'worker' &&
      Array.isArray(assignedProfileIds) &&
      assignedProfileIds.length > 0
        ? (() => {
            const assignedId = assignedProfileIds[0];
            const assignedProfile = config.profiles?.find(p => (p.id ?? p.name) === assignedId);
            return {
              profileKey: assignedProfile?.id ?? assignedId,
              profileId: assignedProfile?.id ?? assignedId,
              profileName: assignedProfile?.name ?? assignedId,
            };
          })()
        : null;

    // Resolver perfil de avisos: worker anclado o admin con perfil activo
    const alertsProfileKey = alertsProfileForWorker?.profileKey ?? manager.selectedUser;
    const alertsProfileId = alertsProfileForWorker?.profileId ?? activeCanonicalProfileId;
    const alertsProfileName = alertsProfileForWorker?.profileName ?? selectedUserName;

    // Hook para gestionar avisos
    const { currentAlert, showAlert, handleDismiss, currentReply, handleDismissReply } = useAlerts(
     workspaceId,
     alertsProfileKey,
     alertsProfileId
   );

      // Determinar si el perfil seleccionado es informativo (solo para workers)
      const selectedProfile = config.profiles?.find(
        profile =>
          String(profile.id) === String(manager.selectedUser) ||
          profile.name === String(manager.selectedUser)
      );

      const isSelectedProfileInformational =
        userRole === 'worker' &&
        Boolean(manager.selectedUser) &&
        Array.isArray(assignedProfileIds) &&
        !assignedProfileIds.map(String).includes(String(manager.selectedUser));


     // Helper para obtener UUID del perfil seleccionado
    const getProfileUuid = () => {
      if (!manager.selectedUser || !config?.profiles) return null;
      const profile = config.profiles.find(p => p.name === manager.selectedUser);
      return profile?.id || null;
    };

   // Función de diagnóstico de sincronización
   // Inicializar push notifications una sola vez (guard con useRef)
   useEffect(() => {
     if (!workspaceId || pushInitializedRef.current) return;
     pushInitializedRef.current = true;
     initializePushNotifications(workspaceId).catch(err => {
       console.warn('[PUSH_INIT] Error initializing push notifications:', err);
     });
     return () => {
       pushInitializedRef.current = false;
     };
   }, [workspaceId]);

  // -------------------------------------------------
  // HIDRATACIÓN DE NOTAS RECURRENTES al navegar/cargar una fecha.
  //
  // Repara recurringNotes/informativeNote/informativeNoteOverride de un día
  // YA MATERIALIZADO en Firestore cuando quedó desactualizado respecto al
  // histórico autoritativo de la plantilla (templates/{profile}.templateDogs),
  // p.ej. si recurringNotes:[] por el bug de reimport de PDF ya corregido en
  // dogDataParsers.js, o por cualquier otra causa histórica.
  //
  // GUARD WORKER: No ejecutar para workers (no tienen permiso para leer templates).
  //
  // Efecto totalmente independiente: no toca handleDaySyncFromCloud, no
  // llama setDaysList, no depende de manager.daysList (ninguna escritura ni
  // snapshot puede re-disparar este efecto). Reutiliza exactamente las
  // mismas primitivas de coordinación que el resto de AppCore
  // (enqueueDocWrite + makeEditLockKey + editLocksRef), sin crear ninguna
  // cola/lock nuevo. La UI se actualiza por el cauce normal ya existente:
  // si hydrateRecurringNotesForExistingDay escribe cambios, el snapshot de
  // subscribeDogs ya activo para esta fecha los recibe y
  // handleDaySyncFromCloud los aplica como cualquier otro cambio remoto.
  useEffect(() => {
    const profile = manager.selectedUser;
    const dayKey = manager.selectedDayKey;
    if (!workspaceId || !profile || !dayKey) return;

    // Guard: No ejecutar para workers (no tienen permiso para leer templates)
    if (userRole === 'worker') {
      return;
    }

    const lockKey = makeEditLockKey(workspaceId, profile, dayKey);

    // Único punto de gestión/log de error para toda la operación (el
    // helper de templateSync.js no captura su propio error, lo propaga).
    enqueueDocWrite(lockKey, async () => {
      const activeLock = editLocksRef.current.get(lockKey);
      if (activeLock && (activeLock.status === 'pending' || activeLock.status === 'committed' || activeLock.status === 'failed')) {
        return; // hay una edición en curso sobre este documento: omitir
      }
      await hydrateRecurringNotesForExistingDay(workspaceId, profile, dayKey);
    }).catch((e) => {
      console.error('[HYDRATE_NOTES_EFFECT_ERR]', e?.message || String(e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, manager.selectedUser, manager.selectedDayKey, userRole]);


  const unsubscribeMap = useRef(new Map());
  const localDogsHydratedRef = useRef(false);
  const effectRunIdRef = useRef(0); // Para tracking de efectos

  function unsubscribeAll() {
    for (const [k, u] of unsubscribeMap.current) { 
      try { 
        u(); 
      } catch(e) {
        console.warn(`[UNSUB_ERROR] ${k}:`, e);
      } 
    }
    unsubscribeMap.current.clear();
  }

  // Función para desconectar Firestore antes de un reset
  const handleDisconnectFirestore = () => {
    console.log("[DISCONNECT] Cancelando todas las suscripciones de Firestore...");
    
    // Cancelar suscripciones de perros
    weekSubscriptions.current.forEach(unsub => {
      if (typeof unsub === "function") {
        unsub();
      }
    });
    weekSubscriptions.current = [];
    
    // Cancelar suscripciones de estados
    const stateSubs = stateSubscriptionsRef.current || {};
    Object.values(stateSubs).forEach(unsub => unsub && unsub());
    stateSubscriptionsRef.current = {};
    
    // Cancelar suscripciones de routeStatus
    const routeSubs = routeStatusSubscriptionsRef.current || {};
    Object.values(routeSubs).forEach(unsub => unsub && unsub());
    routeStatusSubscriptionsRef.current = {};
    
    // Cancelar timers de guardado
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    
    // Cancelar timers de guardado de estados
    if (statesSaveTimer && statesSaveTimer.current) {
      clearTimeout(statesSaveTimer.current);
      statesSaveTimer.current = null;
    }
    
    // Cancelar timers de guardado de routeStatus
    if (routeStatusSaveTimer && routeStatusSaveTimer.current) {
      clearTimeout(routeStatusSaveTimer.current);
      routeStatusSaveTimer.current = null;
    }
    
    // Marcar como no hidratado para prevenir guardados accidentales
    hydrated.current = false;
    applyingRemote.current = false;
    statesApplyingRemote.current = false;
    routeStatusApplyingRemote.current = false;
    
    console.log("[DISCONNECT] ✅ Todas las suscripciones de Firestore canceladas");
  };

  // Registrar la función de desconexión globalmente
  React.useEffect(() => {
    disconnectFirestoreFunction = handleDisconnectFirestore;
    window.getDisconnectFirestore = () => disconnectFirestoreFunction;
    return () => {
      disconnectFirestoreFunction = null;
      window.getDisconnectFirestore = null;
    };
  }, []);

      // Función para cambiar routeStatus
      const handleRouteStatusChange = (status) => {
        // [RT_WORKER_CLICK] Instrumentación: entrada a handleRouteStatusChange
        console.log("[RT_WORKER_CLICK]", {
          userRole,
          selectedUser: manager.selectedUser,
          profileUuid: getProfileUuid(),
          requestedStatus: status,
          dayKey: manager.selectedDayKey,
          workspaceId,
          timestamp: new Date().toISOString()
        });

        const profileUuid = getProfileUuid();
        
        // Actualización OPTIMISTA local - merge con lo existente
        // Nota: usar UUID como clave si está disponible, fallback al nombre
        const statusKey = profileUuid || manager.selectedUser;

        // [RT_STATUS_KEY_RESOLVED] Instrumentación: statusKey resuelto
        console.log("[RT_STATUS_KEY_RESOLVED]", {
          profileUuid,
          selectedUser: manager.selectedUser,
          statusKey,
          source: profileUuid ? "profileUuid" : "selectedUser",
          timestamp: new Date().toISOString()
        });
        setRouteStatus(prev => ({
          ...prev,
          [statusKey]: status,
        }));

        // Persistir en Firestore - POR DÍA
        // ✅ CRÍTICO: Para workers, bloquear escritura hasta que adminUid esté resuelto
        if (workspaceId && manager.selectedUser && manager.selectedDayKey) {
          // Guard para worker: si adminUid aún no está resuelto, NO guardar
          if (userRole === 'worker' && !adminWorkspaceIdRef.current) {
            console.warn('[ROUTE_STATUS] Worker attempting to save before adminUid resolved - blocking write');
            // Revertir optimistic update
            setRouteStatus(prev => {
              const next = { ...prev };
              delete next[statusKey];
              return next;
            });
            return;
          }

          const statusForDay = {
            [statusKey]: status,
          };
          
          // ✅ NUEVO: Para workers, usar adminWorkspaceId resuelto en la suscripción
          const wsIdToUse = (userRole === 'worker' && adminWorkspaceIdRef.current)
            ? adminWorkspaceIdRef.current
            : workspaceId;
          
          // [RT_WRITE_ATTEMPT] Instrumentación temporal para diagnosticar escritura de routeStatus
          console.log("[RT_WRITE_ATTEMPT]", {
            userRole,
            uid: auth.uid || 'unknown',
            workspace: wsIdToUse,
            dayKey: manager.selectedDayKey,
            statusKey,
            payloadKeys: Object.keys(statusForDay || {}),
            payloadExact: statusForDay,
            timestamp: new Date().toISOString()
          });
          
          try {
            saveRouteStatus(wsIdToUse, manager.selectedDayKey, statusForDay, userRole);
            // [RT_WRITE_SUCCESS] Instrumentación: escritura exitosa
            console.log("[RT_WRITE_SUCCESS]", {
              workspace: wsIdToUse,
              dayKey: manager.selectedDayKey,
              statusKey,
              payloadKeys: Object.keys(statusForDay || {}),
              timestamp: new Date().toISOString()
            });
          } catch (err) {
            // [RT_WRITE_ERROR] Instrumentación: error en escritura
            console.error("[RT_WRITE_ERROR]", {
              workspace: wsIdToUse,
              dayKey: manager.selectedDayKey,
              statusKey,
              payloadKeys: Object.keys(statusForDay || {}),
              errorCode: err?.code,
              errorMessage: err?.message || String(err),
              timestamp: new Date().toISOString()
            });
          }
        }
      };

  // Cambiar día localmente (no sincronizamos fecha en Firestore)
  const handleDaySelect = (dayKeyOrDate) => {
    let targetDate;
    if (typeof dayKeyOrDate === "string") {
      try {
        targetDate = makeDayKey(new Date(dayKeyOrDate));
        console.info('[DAYKEY_SRC]', { from: 'AppCore.jsx:handleDaySelect', dayKey: targetDate });
      } catch {
        targetDate = makeDayKey();
      }
    } else if (isValidDate(dayKeyOrDate)) {
      targetDate = makeDayKey(dayKeyOrDate);
    } else {
      targetDate = makeDayKey();
    }
    console.info('[DAYKEY_SRC]', { from: 'AppCore.jsx:handleDaySelect', dayKey: targetDate });
    if (isValidDate(parseDateKey(targetDate))) manager.handleDateChange(parseDateKey(targetDate));
  };

  // Calcular weekKey de forma estable fuera del effect (para dependencias)
  const weekKey = React.useMemo(() => {
    if (!manager.currentDate) return null;
    const weekDays = getWeekDays(manager.currentDate);
    return `W${weekDays[0]?.toISOString().split('T')[0]}`;
  }, [manager.currentDate]);

  // -------------------------------------------------
  // SUSCRIPCIÓN MULTI-DÍA: Sincronización de toda la semana actual
  // - Suscribe a todos los días de la semana
  // - Añade perros que faltan localmente
  // - Actualiza orden y propiedades desde la nube
  // - Nunca elimina automáticamente (solo con acción del usuario)
  // -------------------------------------------------
  useEffect(() => {
    if (!manager.currentDate || !manager.selectedDayKey) return;

    // MODO LOCAL: cargar daysList desde localStorage
    if (LOCAL_ONLY_MODE) {
      console.log("[LOCAL_MODE] Load daysList for profile", manager.selectedUser);
      localDogsHydratedRef.current = false;

      const localDays = loadLocalDaysList(manager.selectedUser) || {};
      setDaysList(localDays);

      console.log("[LOCAL_MODE] Applied daysList from localStorage", {
        profile: manager.selectedUser,
        dayKeys: Object.keys(localDays || {})
      });

      localDogsHydratedRef.current = true;
      return;
    }

    // Limpiar suscripciones anteriores
    unsubscribeAll();

    // Incrementar effectRunId y capturar en variable local
    effectRunIdRef.current++;
    const effectRunId = effectRunIdRef.current;

     // Obtener todos los días de la semana actual
    const weekDays = getWeekDays(manager.currentDate);
    const weekDayKeys = weekDays.map(date => formatDateKey(date));
    
    // LOG: Inicio del efecto de suscripción (weekKey ya calculado en render)
    console.info('[EFFECT_SUBSCRIBE_RUN] ' + JSON.stringify({
      effectRunId,
      workspaceId,
      weekKey,
      selectedDayKey: manager.selectedDayKey,
      daysCount: weekDayKeys.length,
      dayKeysPreview: weekDayKeys.slice(0, 10)
    }));
    
    // LOG de generación de dayKeys/weekKeys
    console.info('[DAYKEY_GENERATION]', {
      tzOffset: new Date().getTimezoneOffset(),
      isoStringNow: new Date().toISOString(),
      weekKey: `W${weekDays[0]?.toISOString().split('T')[0]}`,
      weekDayKeys: weekDayKeys,
      from: 'AppCore:useEffect[subscribeDogs]'
    });

    console.log("[SUB] subscribing to week:", weekDayKeys);

    console.info('[DBG_AUTH_UID]', {
      workspaceId,
      profile: manager?.selectedUser,
      selectedDayKey: manager.selectedDayKey
    });

    if (!workspaceId || !manager.selectedDayKey) {
      console.info('[SUB_SKIP]', { 
        reason: 'no-workspaceId', 
        workspaceId: workspaceId, 
        selectedDayKey: manager.selectedDayKey, 
        hydrated: hydrated.current 
      });
      return;
    }

    // Crear workspace si no existe
    createWorkspaceIfMissing(workspaceId, db).catch(err => {
      console.error("[SUB] Error creating workspace:", err);
    });

    // Suscribirse a cada día de la semana para el perfil actual
    const profileAtSubscribe = manager.selectedUser; // 🔥 CAPTURAR PERFIL EN ESTE MOMENTO

    // ✅ CAMBIO: No llamar extendTemplateWindow al arrancar.
    // Se ejecutará explícitamente solo después de importar un PDF.
    // Esto evita recrear days en Firestore al abrir la app.
    const dogSubscriptions = weekDayKeys.map(dayKey => {
      // Check deduplicación: ¿ya existe suscripción? (KEY COMPUESTA)
      const subKey = `${profileAtSubscribe}::${dayKey}`;
      const alreadyExists = unsubscribeMap.current.has(subKey);
      const reason = alreadyExists ? 'already in map' : 'creating';
      const existingKeys = Array.from(unsubscribeMap.current.keys()).slice(0, 10);
      
      console.info('[SUBSCRIBE_DEDUP_CHECK] ' + JSON.stringify({
        effectRunId,
        dayKey,
        profile: profileAtSubscribe,
        reason,
        isAlreadySubscribed: alreadyExists,
        mapSize: unsubscribeMap.current.size,
        existingKeysSample: existingKeys,
        workspaceId,
        daysCount: weekDayKeys.length
      }));
      
      if (alreadyExists) {
        // Usar la suscripción existente
        return { dayKey, subKey, unsub: unsubscribeMap.current.get(subKey) };
      }
      
      // LOG: Antes de suscribirse a un dayKey específico
      console.info('[EFFECT_SUBSCRIBE_DAY] ' + JSON.stringify({
        effectRunId,
        dayKey,
        profile: profileAtSubscribe
      }));
      
      const unsub = subscribeDogs(workspaceId, dayKey, (cloudDogs, snapshotMetadata) => {
        // LOG 1: [SNAP_RECEIVED] - Justo al recibir snapshot de perros
        console.info('[SNAP_RECEIVED]', JSON.stringify({
          profile: profileAtSubscribe,
          dayKey,
          dogsLen: Array.isArray(cloudDogs) ? cloudDogs.length : null,
          workspaceId,
          fromCache: snapshotMetadata?.fromCache,
          hasPendingWrites: snapshotMetadata?.hasPendingWrites
        }));
        // 🔥 PASAR profileAtSubscribe + weekKey para validación tardía
        // + snapshotMetadata para distinguir remoto confirmado de cache/pending
        handleDaySyncFromCloud(dayKey, cloudDogs, profileAtSubscribe, weekKey, snapshotMetadata);
      }, { 

        includeMetadataChanges: true,
        visibleProfile: profileAtSubscribe 
      });

      console.info('[SUBSCRIBE_CREATED]', { workspaceId, dayKey, profile: profileAtSubscribe });
      return { dayKey, subKey, unsub };
    });

    // Guardar referencias de desuscripción (CON CLAVE COMPUESTA)
    dogSubscriptions.forEach(({ dayKey, subKey, unsub }) => {
      // LOG 4: [SUB_KEY] - Antes de guardar en el map
      console.error('[SUB_KEY]', JSON.stringify({
        subKey,
        profileAtSubscribe,
        dayKey,
        weekKeyAtSubscribe: weekKey,
        mapOperation: 'set'
      }));
      
      unsubscribeMap.current.set(subKey, unsub);
    });

    return () => {
      // LOG: Cleanup del efecto
      console.info('[EFFECT_SUBSCRIBE_CLEANUP] ' + JSON.stringify({
        effectRunId,
        cleanupProfile: profileAtSubscribe,
        keysBeforeCleanup: Array.from(unsubscribeMap.current.keys()).filter(k => k.includes(profileAtSubscribe))
      }));
      
      // 🔥 CRÍTICO: Desuscribir SOLO las keys de este perfil+semana (no "bruto")
      dogSubscriptions.forEach(({ subKey, unsub }) => {
        unsubscribeMap.current.delete(subKey);
        unsub();
      });
      console.log("[SUB] unsubscribed from week:", { profile: profileAtSubscribe, weekKey });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, manager.selectedUser]);

  // -------------------------------------------------
  // SUSCRIPCIÓN ESTADOS: Sincronización de completedDogs y urgentDogs
  // - Suscribe a todos los días de la semana actual
  // - Los cambios se sincronizan en tiempo real entre ventanas
  // - Las suscripciones se mantienen estables mientras manager.currentDate y selectedUser no cambien
  // -------------------------------------------------
  useEffect(() => {
    if (!manager.currentDate) return;

    // MODO LOCAL: no suscribirse a Firebase para estados
    if (LOCAL_ONLY_MODE) {
      console.log("[LOCAL_MODE] States managed locally (no Firebase subscription)");
      return; // NO suscribirse a Firebase para estados
    }

     if (!workspaceId) {
       console.info('[SUB_STATES_SKIP]', { 
         reason: 'no-workspaceId'
       });
       return;
     }

     // Obtener todos los días de la semana actual
     const weekDays = getWeekDays(manager.currentDate);
     const weekDayKeys = weekDays.map(date => formatDateKey(date));
     
     // LOG de generación de dayKeys/weekKeys para estados
     console.info('[DAYKEY_GENERATION]', {
       tzOffset: new Date().getTimezoneOffset(),
       isoStringNow: new Date().toISOString(),
       weekKey: `W${weekDays[0]?.toISOString().split('T')[0]}`,
       weekDayKeys: weekDayKeys,
       from: 'AppCore:useEffect[subscribeStates]'
     });

     console.log("[SUB_STATES] subscribing to week states:", weekDayKeys, "for workspaceId:", workspaceId, "profile:", manager.selectedUser);

    // Limpiar suscripciones ANTIGUAS (de otras semanas)
    const oldDayKeys = Object.keys(stateSubscriptionsRef.current || {});
    const dayKeysToRemove = oldDayKeys.filter(k => !weekDayKeys.includes(k));
    dayKeysToRemove.forEach(dayKey => {
      const unsub = stateSubscriptionsRef.current[dayKey];
      if (unsub) {
        unsub();
        delete stateSubscriptionsRef.current[dayKey];
        unsubscribeMap.current.delete(dayKey);
        console.log("[SUB_STATES] unsubscribed from old dayKey:", dayKey);
      }
    });

    // Suscribirse a nuevos días de la semana (si no está ya suscrito)
    weekDayKeys.forEach(dayKey => {
      if (stateSubscriptionsRef.current[dayKey]) {
        console.log("[SUB_STATES] already subscribed to dayKey:", dayKey);
        return; // Ya estamos suscrito a este día
      }

      const unsub = subscribeStates(workspaceId, dayKey, ({ completedDogs: cloudCompleted, urgentDogs: cloudUrgent }) => {
        console.log("[STATE_SUB_CALLBACK] received remote states for dayKey:", dayKey, {
          completedCount: Object.keys(cloudCompleted || {}).length,
          urgentCount: Object.keys(cloudUrgent || {}).length
        });
        handleStatesSyncFromCloud(dayKey, cloudCompleted, cloudUrgent);
      }, { 
        includeMetadataChanges: true,
        visibleProfile: manager.selectedUser
      });

      console.info('[SUB_STATES_START]', { workspaceId, dayKey });
      stateSubscriptionsRef.current[dayKey] = unsub;
      unsubscribeMap.current.set(dayKey, unsub);
    });

     return () => {
       // Al cambiar de semana o workspaceId, limpiar todas las suscripciones
       const stateSubs = stateSubscriptionsRef.current || {};
       Object.values(stateSubs).forEach(unsub => unsub && unsub());
       stateSubscriptionsRef.current = {};
       // NO limpiar unsubscribeMap aquí para evitar interferir con otras suscripciones
     };
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manager.currentDate, manager.selectedUser, workspaceId]);

  // -------------------------------------------------
  // SUSCRIPCIÓN ROUTE STATUS: Sincronización de estados de ruta POR DÍA
  // Se suscribe al workspace root pero filtra por el dayKey actual
  // -------------------------------------------------
   useEffect(() => {
      // [RT_EFFECT_ENTER] Instrumentación: entrada al effect
      console.log("[RT_EFFECT_ENTER]", {
        userRole,
        uid: auth?.currentUser?.uid || 'unknown',
        workspaceId,
        dayKey: manager.selectedDayKey,
        timestamp: new Date().toISOString()
      });

      if (!workspaceId || !manager.selectedDayKey) {
        // [RT_EFFECT_GUARD] Instrumentación: retorno anticipado
        console.log("[RT_EFFECT_GUARD]", {
          reason: !workspaceId ? 'no-workspaceId' : 'no-selectedDayKey',
          workspaceId: !!workspaceId,
          dayKey: !!manager.selectedDayKey,
          timestamp: new Date().toISOString()
        });
        console.info('[SUB_ROUTE_STATUS_SKIP]', { workspaceId: !!workspaceId, dayKey: !!manager.selectedDayKey });
        return;
      }

     console.log("[SUB_ROUTE_STATUS] subscribing to dayKey:", manager.selectedDayKey);

       // ✅ Para trabajadores: obtener adminUid desde workerBindings
       let adminWorkspaceId = workspaceId;
       console.log("[DBG_SUB_SETUP] setupSubscription context:", {
         userRole,
         workspaceId,
         selectedDayKey: manager.selectedDayKey,
         isWorker: userRole === 'worker'
       });
      const setupSubscription = async () => {
        // [RT_SETUP_ENTER] Instrumentación: entrada a setupSubscription
        console.log("[RT_SETUP_ENTER]", {
          userRole,
          workspace: adminWorkspaceId,
          dayKey: manager.selectedDayKey,
          timestamp: new Date().toISOString()
        });

         if (userRole === 'worker' && auth.currentUser) {
           try {
             const bindingRef = doc(db, 'workerBindings', auth.currentUser.uid);
             // [RT_BINDING_READ_START] Instrumentación: inicio de lectura
             console.log("[RT_BINDING_READ_START]", {
               workerUid: auth.currentUser.uid,
               path: bindingRef.path,
               timestamp: new Date().toISOString()
             });
             const bindingSnap = await getDoc(bindingRef);
             if (bindingSnap.exists()) {
               // [RT_BINDING_READ_RESULT] Instrumentación: documento existe
               const snapData = bindingSnap.data();
               console.log("[RT_BINDING_READ_RESULT]", {
                 exists: true,
                 fieldNames: Object.keys(snapData || {}),
                 hasAdminUid: !!snapData?.adminUid,
                 timestamp: new Date().toISOString()
               });
               const adminUid = snapData?.adminUid;
               if (adminUid) {
                 adminWorkspaceId = adminUid;
                 // [RT_BINDING_RESOLVED] Instrumentación: binding resuelto
                 console.log("[RT_BINDING_RESOLVED]", {
                   workerUid: auth.currentUser.uid,
                   adminUid: adminUid,
                   workspace: adminWorkspaceId,
                   dayKey: manager.selectedDayKey,
                   timestamp: new Date().toISOString()
                 });
                 // ✅ NUEVO: Guardar adminUid resuelto para reutilizar en handleRouteStatusChange
                 adminWorkspaceIdRef.current = adminUid;
                 console.log("[SUB_ROUTE_STATUS] worker using adminWorkspaceId:", adminWorkspaceId);
               } else {
                 // [RT_BINDING_MISSING] Instrumentación: documento existe pero falta adminUid
                 console.log("[RT_BINDING_MISSING]", {
                   exists: true,
                   reason: "missing-adminUid-field",
                   fieldNames: Object.keys(snapData || {}),
                   timestamp: new Date().toISOString()
                 });
               }
             } else {
               // [RT_BINDING_MISSING] Instrumentación: documento no existe
               console.log("[RT_BINDING_MISSING]", {
                 exists: false,
                 reason: "document-not-found",
                 timestamp: new Date().toISOString()
               });
             }
           } catch (err) {
             // [RT_BINDING_READ_ERROR] Instrumentación: error en lectura
             console.error("[RT_BINDING_READ_ERROR]", {
               code: err?.code,
               message: err?.message,
               timestamp: new Date().toISOString()
             });
             console.error("[SUB_ROUTE_STATUS] error reading workerBinding:", err);
             adminWorkspaceId = workspaceId;
             adminWorkspaceIdRef.current = null;
           }
         } else if (userRole === 'worker') {
           // [RT_SETUP_ABORT] Instrumentación: retorno anticipado
           console.log("[RT_SETUP_ABORT]", {
             reason: "no-currentUser",
             timestamp: new Date().toISOString()
           });
         }
        
        console.log("[DBG_SUBSCRIBE_INIT] subscribing to routeStatus with:", {
          adminWorkspaceId,
          dayKey: manager.selectedDayKey,
          userRole
        });
        
        // [RT_SUBSCRIBE] Instrumentación temporal al crear suscripción
        console.log("[RT_SUBSCRIBE]", {
          userRole,
          uid: auth.uid || 'unknown',
          workspace: adminWorkspaceId,
          dayKey: manager.selectedDayKey
        });

        const unsub = subscribeRouteStatus(adminWorkspaceId, manager.selectedDayKey, (statusForDay) => {
          // [RT_SNAPSHOT] Instrumentación temporal para diagnosticar snapshot de routeStatus
          console.log("[RT_SNAPSHOT]", {
            workspace: adminWorkspaceId,
            dayKey: manager.selectedDayKey,
            keys: Object.keys(statusForDay || {}),
            statusData: statusForDay
          });

          // [TEMP_ROUTE_CALLBACK] Instrumentación temporal para diagnosticar entrada de routeStatus
          console.log("[TEMP_ROUTE_CALLBACK] received from subscribeRouteStatus:", {
            dayKey: manager.selectedDayKey,
            selectedUserAtCallback: manager.selectedUser,
            keys: Object.keys(statusForDay || {}),
            davidStatus: statusForDay?.["89cb72c5-c543-4022-84fb-68ab7e5fdeb7"],
            saraStatus: statusForDay?.["89cb72c5-c543-4022-84fb-68ab7e5fdeb7"],
            allStatusData: statusForDay
          });
          handleRouteStatusSyncFromCloud(statusForDay);
       }, { 
         includeMetadataChanges: true,
         userRole
       });

        // [RT_SUB_CREATED] Instrumentación: suscripción creada
        console.log("[RT_SUB_CREATED]", {
          workspace: adminWorkspaceId,
          dayKey: manager.selectedDayKey,
          unsubType: typeof unsub,
          timestamp: new Date().toISOString()
        });

       routeStatusSubscriptionsRef.current['global'] = unsub;
       unsubscribeMap.current.set('routeStatus', unsub);

       // [RT_SUB_STORED] Instrumentación: unsub almacenado en ref
       console.log("[RT_SUB_STORED]", {
         key: 'global',
         unsubExists: !!unsub,
         workspace: adminWorkspaceId,
         dayKey: manager.selectedDayKey,
         timestamp: new Date().toISOString()
       });
      };

     setupSubscription();

      return () => {
        // [RT_SUB_CLEANUP] Instrumentación: cleanup del effect
        console.log("[RT_SUB_CLEANUP]", {
          workspace: workspaceId,
          dayKey: manager.selectedDayKey,
          unsubExists: !!routeStatusSubscriptionsRef.current?.global,
          timestamp: new Date().toISOString()
        });
        const routeSubs = routeStatusSubscriptionsRef.current || {};
        Object.values(routeSubs).forEach(unsub => unsub && unsub());
        routeStatusSubscriptionsRef.current = {};
        unsubscribeMap.current.delete('routeStatus');
        console.log("[SUB_ROUTE_STATUS] unsubscribed");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager.selectedDayKey, userRole]); // Depende del dayKey actual y del rol para readecuar workspace

   // Manejador de sincronización de routeStatus GLOBAL desde la nube
  const handleRouteStatusSyncFromCloud = (allRouteStatus) => {
    console.log("[SYNC_ROUTE_STATUS] received global status from cloud:", allRouteStatus);
    
    // Marcar como hidratado en la primera carga
    if (!routeStatusHydrated.current) {
      routeStatusHydrated.current = true;
      console.log("[ROUTE_HYDRATE] ✅ routeStatus hydrated from Firestore:", allRouteStatus);
    }
    
    // Solo aplicar cambios si provienen de la nube (no de guardado local)
    // Comparar con el estado actual para evitar sobrescribir cambios locales pendientes
    if (!routeStatusApplyingRemote.current) {
      routeStatusApplyingRemote.current = true;
      // Eliminar _routeStatusUpdatedAt antes de guardar en estado local:
      // ese campo cambia en cada save y dispararía otro save → loop infinito
      const { _routeStatusUpdatedAt, ...statusWithoutMetadata } = allRouteStatus || {};
      // [TEMP_ROUTE_SET] Instrumentación temporal antes de setRouteStatus
      console.log("[TEMP_ROUTE_SET] before setRouteStatus:", {
        dayKey: manager.selectedDayKey,
        keys: Object.keys(statusWithoutMetadata || {}),
        davidStatus: statusWithoutMetadata?.["89cb72c5-c543-4022-84fb-68ab7e5fdeb7"]
      });
      setRouteStatus(statusWithoutMetadata);
      setTimeout(() => {
        routeStatusApplyingRemote.current = false;
      }, 200);
    }
  };

  // Función auxiliar para manejar sincronización de estados desde la nube
  const handleStatesSyncFromCloud = (dayKey, cloudCompleted, cloudUrgent) => {
    // 🔒 Guard: si hubo un cambio local en los últimos 600ms, ignorar snapshot de la nube.
    // Evita que un eco de Firestore revierta el estado optimista del usuario.
    const msSinceLocalChange = Date.now() - lastLocalStateChangeRef.current;
    if (msSinceLocalChange < 600) {
      return;
    }

    console.log(`[SUB_STATES] day ${dayKey}: received states`);

    // Filtrar solo las claves que corresponden a este día
    const relevantCompleted = {};
    const relevantUrgent = {};

    Object.entries(cloudCompleted || {}).forEach(([key, value]) => {
      if (key.startsWith(dayKey)) {
        relevantCompleted[key] = value;
      }
    });

    Object.entries(cloudUrgent || {}).forEach(([key, value]) => {
      if (key.startsWith(dayKey)) {
        relevantUrgent[key] = value;
      }
    });

    // Obtener estados locales actuales
    const localCompleted = manager.completedDogs || {};
    const localUrgent = manager.urgentDogs || {};

    // Comparar solo los estados del dayKey actual con lo que llega de la nube
    const currentDayCompletedLocal = Object.fromEntries(
      Object.entries(localCompleted).filter(([k]) => k.startsWith(dayKey))
    );
    const currentDayUrgentLocal = Object.fromEntries(
      Object.entries(localUrgent).filter(([k]) => k.startsWith(dayKey))
    );

    const completedChanged = JSON.stringify(relevantCompleted) !== JSON.stringify(currentDayCompletedLocal);
    const urgentChanged = JSON.stringify(relevantUrgent) !== JSON.stringify(currentDayUrgentLocal);

    // Functional updater: usa estado FRESCO (no closure stale)
    // Bail-out automático si cloud == local actual → no sobrescribe, no re-render
    setCompletedDogs(prevCompleted => {
      const localDayCompleted = Object.fromEntries(
        Object.entries(prevCompleted || {}).filter(([k]) => k.startsWith(dayKey))
      );
      if (JSON.stringify(relevantCompleted) === JSON.stringify(localDayCompleted)) {
        return prevCompleted; // ya sincronizado, no tocar
      }
      console.log('[STATE_SYNC] applying cloud completedDogs for', dayKey);
      return { ...(prevCompleted || {}), ...relevantCompleted };
    });
    setUrgentDogs(prevUrgent => {
      const localDayUrgent = Object.fromEntries(
        Object.entries(prevUrgent || {}).filter(([k]) => k.startsWith(dayKey))
      );
      if (JSON.stringify(relevantUrgent) === JSON.stringify(localDayUrgent)) {
        return prevUrgent; // ya sincronizado, no tocar
      }
       console.log('[STATE_SYNC] applying cloud urgentDogs for', dayKey);
      return { ...(prevUrgent || {}), ...relevantUrgent };
    });

  };

  // Función auxiliar para manejar sincronización de un día específico
  const handleDaySyncFromCloud = (key, cloudDogs, profileApplied, weekKeyAtSubscribe, snapshotMetadata) => {
    const cacheKey = dogKey(profileApplied, key);
    // Snapshot remoto CONFIRMADO por el servidor: ni de caché local ni con
    // escrituras locales pendientes. Solo un snapshot así puede autorizar
    // que un array vacío reemplace datos locales existentes (ver rama
    // "cloud vacío / local no vacío" más abajo).
    const isConfirmedServerSnapshot = !!snapshotMetadata
      && snapshotMetadata.fromCache === false
      && snapshotMetadata.hasPendingWrites === false;
    
    // LOG 2A: [SYNC_APPLY] - Al inicio, para ver qué llega
    console.error('[SYNC_APPLY]', JSON.stringify({
      phase: 'entry',
      profileApplied,
      currentProfile: manager.selectedUser,
      dayKey: key,
      dogsLen: Array.isArray(cloudDogs) ? cloudDogs.length : null,
      weekKeyAtSubscribe,
      fromCache: snapshotMetadata?.fromCache,
      hasPendingWrites: snapshotMetadata?.hasPendingWrites,
      isConfirmedServerSnapshot,
      currentWeekKey: (() => {
        const wd = getWeekDays(manager.currentDate);
        return `W${wd[0]?.toISOString().split('T')[0]}`;
      })()
    }));

    
    // 🔥 CRÍTICO: GUARD 1 - Validar que el perfil no ha cambiado
    if (profileApplied !== manager.selectedUser) {
      console.error('[SYNC_SKIP]', JSON.stringify({
        reason: 'profile-mismatch',
        dayKey: key,
        cacheKey,
        profileApplied,
        currentUser: manager.selectedUser,
        dogsLen: cloudDogs?.length || 0
      }));
      return;
    }

    // 🔥 CRÍTICO: GUARD 2 - Validar que la weekKey no ha cambiado (snapshot tardío)
    const weekDays = getWeekDays(manager.currentDate);
    const currentWeekKey = `W${weekDays[0]?.toISOString().split('T')[0]}`;
    if (weekKeyAtSubscribe !== currentWeekKey) {
      console.info('[SYNC_SKIP]', JSON.stringify({
        reason: 'week-mismatch',
        dayKey: key,
        cacheKey,
        weekKeyAtSubscribe,
        currentWeekKey,
        dogsLen: cloudDogs?.length || 0
      }));
      return;
    }

    // ✅ Coordinación (weekOfChange): gestión del lock de edición ANTES de
    // isIdenticalToLastSave, cloud.length>0 y cloud vacío. Debe funcionar
    // también con cloud=[]. Si no hay lock para este documento, el camino
    // normal de abajo queda 100% intacto (comportamiento actual).
    {
       const lockKey = workspaceId
         ? makeEditLockKey(workspaceId, profileApplied, key)
         : null;
      const lock = lockKey ? editLocksRef.current.get(lockKey) : null;

      if (lock) {
        const cloudRaw = cloudDogs || [];
        const compositeKeyLock = `${profileApplied}::${key}`;
        const expectedPresence = lock.expectedPresence;
        const expectedFields = lock.expectedFields;

        if (lock.status === 'failed') {
          // Aplicar el RAW del servidor tal cual, incluso si está vacío.
          // No aplicar overlay. Borrar el lock de la misma revisión.
          setDaysList(prevDaysList => {
            const finalArr = cloudRaw.map(d => ({ ...d }));
            const nextDaysList = { ...prevDaysList, [compositeKeyLock]: finalArr };

            // [SNAPSHOT_APPLIED_TO_DAYSLISTREF]: instrumentación temporal
            console.log('[SNAPSHOT_APPLIED_TO_DAYSLISTREF]', JSON.stringify({
              profile: profileApplied,
              dayKey: key,
              branch: 'lock-failed',
              prevOrder: (prevDaysList?.[compositeKeyLock] || []).map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              cloudOrder: cloudRaw.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              nextOrder: finalArr.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              timestamp: Date.now()
            }));

            daysListRef.current = nextDaysList;

            if (lastSavedDaysListRef?.current) {
              lastSavedDaysListRef.current[compositeKeyLock] = finalArr;
            }
            const currentLock = editLocksRef.current.get(lockKey);
            if (currentLock && currentLock.revision === lock.revision) {
              editLocksRef.current.delete(lockKey);
            }
            return nextDaysList;
          });
          if (!hydrated.current) hydrated.current = true;
          return;
        }

        // status: 'pending' o 'committed'
        const rawMatchedDog = cloudRaw.find((d) => matchesLockDogEntry(d, lock)) || null;
        const rawContentConfirmed = expectedPresence
          ? !!rawMatchedDog
            && !!expectedFields
            && rawMatchedDog.informativeNote === expectedFields.informativeNote
            && rawMatchedDog.informativeNoteOverride === expectedFields.informativeNoteOverride
          : !rawMatchedDog;

        if (lock.status === 'pending' && rawContentConfirmed) {
          // No liberar. Guardar rawConfirmed y continuar aplicando overlay
          // (cae al bloque de overlay más abajo, ya que status sigue
          // siendo 'pending' y aún no se ha limpiado el lock).
          lock.rawConfirmed = true;
          lock.rawConfirmedRevision = lock.revision;
        }

        if (lock.status === 'committed' && rawContentConfirmed) {
          // Sustituir en su MISMA posición (map), o filtrar defensivamente
          // si expectedPresence=false. Nunca filter+append.
          setDaysList(prevDaysList => {
            let finalDayArr;
            if (expectedPresence) {
              let replaced = false;
              finalDayArr = cloudRaw.map((d) => {
                if (!replaced && matchesLockDogEntry(d, lock)) {
                  replaced = true;
                  return { ...lock.committedRecord };
                }
                return { ...d };
              });
            } else {
              finalDayArr = cloudRaw
                .filter((d) => !matchesLockDogEntry(d, lock))
                .map(d => ({ ...d }));
            }

            const nextDaysList = { ...prevDaysList, [compositeKeyLock]: finalDayArr };
            daysListRef.current = nextDaysList;

            // [SNAPSHOT_APPLIED_TO_DAYSLISTREF]: instrumentación temporal
            console.log('[SNAPSHOT_APPLIED_TO_DAYSLISTREF]', JSON.stringify({
              profile: profileApplied,
              dayKey: key,
              branch: 'lock-committed-confirmed',
              prevOrder: (prevDaysList?.[compositeKeyLock] || []).map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              cloudOrder: cloudRaw.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              nextOrder: finalDayArr.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
              timestamp: Date.now()
            }));

            if (lastSavedDaysListRef?.current) {
              lastSavedDaysListRef.current[compositeKeyLock] = finalDayArr;
            }
            const currentLock = editLocksRef.current.get(lockKey);
            if (currentLock && currentLock.revision === lock.revision) {
              editLocksRef.current.delete(lockKey);
            }
            return nextDaysList;
          });
          if (!hydrated.current) hydrated.current = true;
          return;
        }

        // Mientras pending o committed SIN confirmar: aplicar overlay,
        // sin borrar el lock.
        setDaysList(prevDaysList => {
          let dogsToApply;
          if (expectedPresence) {
            const idxInCloud = cloudRaw.findIndex((d) => matchesLockDogEntry(d, lock));
            if (idxInCloud !== -1) {
              dogsToApply = cloudRaw.map((d, i) =>
                i === idxInCloud ? { ...d, ...expectedFields } : { ...d }
              );
            } else {
              // Recuperarlo desde el estado local vigente y aplicar expectedFields.
              const currentLocalArr = prevDaysList?.[compositeKeyLock] || [];
              const localRecord = currentLocalArr.find((d) => matchesLockDogEntry(d, lock));
              const recovered = localRecord ? { ...localRecord, ...expectedFields } : null;
              dogsToApply = recovered
                ? [...cloudRaw.map(d => ({ ...d })), recovered]
                : cloudRaw.map(d => ({ ...d }));
            }
          } else {
            // Eliminarlo de dogsToApply; nunca reinsertarlo desde local.
            dogsToApply = cloudRaw
              .filter((d) => !matchesLockDogEntry(d, lock))
              .map(d => ({ ...d }));
          }

          const nextDaysList = { ...prevDaysList, [compositeKeyLock]: dogsToApply };
          daysListRef.current = nextDaysList;

          // [SNAPSHOT_APPLIED_TO_DAYSLISTREF]: instrumentación temporal
          console.log('[SNAPSHOT_APPLIED_TO_DAYSLISTREF]', JSON.stringify({
            profile: profileApplied,
            dayKey: key,
            branch: `lock-overlay-${lock.status}`,
            prevOrder: (prevDaysList?.[compositeKeyLock] || []).map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
            cloudOrder: cloudRaw.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
            nextOrder: dogsToApply.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
            timestamp: Date.now()
          }));

          if (lastSavedDaysListRef?.current) {
            lastSavedDaysListRef.current[compositeKeyLock] = dogsToApply;
          }
          // No borrar el lock: sigue pending (o committed sin confirmar).
          return nextDaysList;
        });
        if (!hydrated.current) hydrated.current = true;
        return;
      }
    }

    const cloud = cloudDogs || [];
    const currentLocal = getDogsFor(daysListRef.current, profileApplied, key);
    
    // Comparar con lo que acabamos de guardar para evitar loops.

    // Clave unificada: SIEMPRE `${profile}::${dateKey}` (lectura y escritura
    // deben coincidir exactamente con la clave usada en persistAddedDogToFirebase
    // y en el bloque de autoguardado de más abajo).
    const lastSavedForDay = lastSavedDaysListRef.current[`${profileApplied}::${key}`];
    const isIdenticalToLastSave = lastSavedForDay && 
      areSyncPayloadsEqual(lastSavedForDay, cloud);

    const isLocalAlreadySynchronized =
      areSyncPayloadsEqual(currentLocal, cloud);

    console.log('[SYNC_GUARD_DBG]', JSON.stringify({
      profile: profileApplied,
      dayKey: key,
      hasLastSaved: !!lastSavedForDay,
      isIdenticalToLastSave,
      isLocalAlreadySynchronized,
      timestamp: Date.now(),
    }));

    // Si es idéntico a lo que acabamos de guardar, es un reflejo de nuestro cambio
    if (isIdenticalToLastSave && isLocalAlreadySynchronized) {

      console.log(`[SYNC] skipped: identical to last save (loop prevention)`);
      if (!hydrated.current) hydrated.current = true;
      return;
    }
    
    // 🔒 VALIDACIÓN DE SEGURIDAD: Firestore siempre tiene prioridad SALVO que esté vacío por error
    
    // Si Firestore tiene datos, SIEMPRE usarlos (fuente de verdad)
    if (cloud.length > 0) {
      console.log(`[SYNC] ✅ Firestore tiene ${cloud.length} perros para ${key}, aplicando`);
      
      // LOG al hacer setDaysList desde snapshot
      console.info('[SETDAYLIST_APPLY]', {
        source: 'snapshot',
        dayKey: key,
        count: cloud.length,
        tzOffset: new Date().getTimezoneOffset(),
        isoStringNow: new Date().toISOString()
      });
      
      const cacheKey = dogKey(profileApplied, key);
      console.info('[SYNC_APPLY]', JSON.stringify({ profile: profileApplied, dayKey: key, dogsLen: cloud.length, cacheKey }));
      
      // LOG 3: [DAYS_WRITE] - Justo antes de escribir daysList (cloud.length > 0)
      console.error('[DAYS_WRITE]', JSON.stringify({
        keyUsed: cacheKey,
        profile: profileApplied,
        dogsLen: cloud.length,
        isCompositKey: true
      }));
      
      const traceDogs = cloud.filter(d => d?.name === 'POMPON' || d?.name === 'MUSHU' || d?.name === 'PIPPO' || d?.name === 'MURA' || d?.name === 'RIO' || d?.name === 'CLAY' || d?.name === 'MOWGLY' || d?.name === 'LARA');


      traceDogs.forEach(traceDog => {
        console.log('[NOTE_TRACE]', JSON.stringify({
          source: 'SNAPSHOT_RECEIVED',
          dogId: traceDog.id || null,
          name: traceDog.name,
          dayKey: key,
          note: traceDog.informativeNote,
          override: traceDog.informativeNoteOverride,
          isIdenticalToLastSave,
          ts: Date.now(),
        }));
      });

      setDaysList(prevDaysList => ({
        ...prevDaysList,
        [cacheKey]: cloud.map(dog => ({ ...dog }))
      }));
      
      if (!hydrated.current) hydrated.current = true;
      return;
    }
    
    // Si Firestore está vacío pero local tiene datos

    if (cloud.length === 0 && currentLocal.length > 0) {
      const compositeKeyDeletedCheck = `${profileApplied}::${key}`;
      if (deletedAllKeysRef.current.has(compositeKeyDeletedCheck)) {
        // ✅ Vacío intencionado: el propio flujo de borrado (DELETE_SCOPE.ALL)
        // marcó esta combinación profile::dayKey ANTES de que Firestore
        // emitiera este snapshot. Aceptar el vacío en vez de conservar el
        // estado local (que aún no reflejaba el borrado por timing de React).
        setDaysList(prevDaysList => {
          const nextDaysList = { ...prevDaysList, [compositeKeyDeletedCheck]: [] };
          daysListRef.current = nextDaysList;
          if (lastSavedDaysListRef?.current) {
            lastSavedDaysListRef.current[compositeKeyDeletedCheck] = [];
          }
          return nextDaysList;
        });
        if (!hydrated.current) hydrated.current = true;
        // Limpiar la marca SOLO después de haber incorporado el vacío al
        // estado y a las referencias (no antes, para no reabrir la carrera).
        clearDeletedAll(profileApplied, key);
        return;
      }
      // Snapshot remoto CONFIRMADO por el servidor (no cache, no pending
      // writes): el vacío representa un borrado real ya persistido en
      // Firestore (por este mismo cliente o por otro Admin). Aplicarlo,
      // igual que la rama DELETE_SCOPE.ALL de arriba, para que el borrado
      // remoto se refleje en tiempo real sin esperar a un reload completo.
      if (isConfirmedServerSnapshot) {
        console.warn(`[SYNC] ✅ Firestore vacío CONFIRMADO por servidor (fromCache=false, hasPendingWrites=false). Aplicando borrado remoto sobre ${currentLocal.length} perros locales.`);
        setDaysList(prevDaysList => {
          const nextDaysList = { ...prevDaysList, [compositeKeyDeletedCheck]: [] };
          daysListRef.current = nextDaysList;
          if (lastSavedDaysListRef?.current) {
            lastSavedDaysListRef.current[compositeKeyDeletedCheck] = [];
          }
          return nextDaysList;
        });
        if (!hydrated.current) hydrated.current = true;
        return;
      }

      console.warn(`[SYNC] ⚠️ Firestore vacío (fromCache=${snapshotMetadata?.fromCache}, hasPendingWrites=${snapshotMetadata?.hasPendingWrites}) pero local tiene ${currentLocal.length} perros. Conservando local: snapshot no confirmado por servidor.`);
      // NO sobrescribir con array vacío - snapshot cacheado/transitorio o con
      // escrituras locales pendientes, no confirmado aún por el servidor.
      if (!hydrated.current) hydrated.current = true;
      return;
    }


    
    // Ambos vacíos: OK, aplicar vacío CON COMPOSITE KEY
    console.log(`[SYNC] Ambos vacíos para ${key} - aplicando array vacío con composite key`);
    
    // LOG al hacer setDaysList con array vacío
    console.info('[SETDAYLIST_APPLY]', {
      source: 'snapshot',
      dayKey: key,
      count: 0,
      tzOffset: new Date().getTimezoneOffset(),
      isoStringNow: new Date().toISOString()
    });
    
    // LOG 3b: [DAYS_WRITE] - Justo antes de escribir daysList (ambos vacíos) CON COMPOSITE KEY
    const k = `${profileApplied}::${key}`;
    console.error('[DAYS_WRITE]', JSON.stringify({
      keyUsed: k,
      profile: profileApplied,
      dogsLen: 0,
      isCompositKey: true
    }));
    
    setDaysList(prevDaysList => ({
      ...prevDaysList,
      [k]: []
    }));
    
    if (!hydrated.current) hydrated.current = true;
  };

  // -------------------------------------------------
  // GUARDADO: sube TODOS los días de la semana a Firestore con debounce
  // -------------------------------------------------
  const daysListKey = JSON.stringify(manager.daysList);

  useEffect(() => {
    if (!manager.currentDate || !manager.selectedDayKey) return;

    if (applyingRemote.current) {
      console.log("[SAVE] skipped: applying remote changes");
      return;
    }

    // MODO LOCAL: guardar en localStorage con debounce
    if (LOCAL_ONLY_MODE) {
      if (!localDogsHydratedRef.current) {
        console.log("[LOCAL_MODE] ⏭️ Skip save: daysList not hydrated yet for profile", manager.selectedUser);
        return;
      }

      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const dayKeys = Object.keys(manager.daysList || {});
        console.log("[LOCAL_MODE] Saving daysList to localStorage", {
          profile: manager.selectedUser,
          totalDays: dayKeys.length
        });
        saveLocalDaysList(manager.selectedUser, manager.daysList);
      }, 300);

      return () => clearTimeout(saveTimer.current);
    }

  // ✅ Fix quirúrgico: Solo guardar semana visible usando dateKeys válidos
  const daysList = manager.daysList || {};
  const profile = manager.selectedUser;
  
  // Obtener todos los días de la semana visible
  const weekDays = getWeekDays(manager.currentDate);
  const weekDayKeys = weekDays.map(d => formatDateKey(d)); // dateKey: "YYYY-MM-DD"
  
  // LOG [AUTO_SAVE_DBG]: Inicio del cálculo de guardado
  console.error("[AUTO_SAVE_DBG] INICIO", JSON.stringify({
    profile,
    weekDayKeys,
    weekDayKeysCount: weekDayKeys.length,
    timestamp: new Date().toISOString()
  }));

   clearTimeout(saveTimer.current);
   saveTimer.current = setTimeout(async () => {
     // LOG [AUTO_SAVE_DBG]: Antes de guardar
    console.error("[AUTO_SAVE_DBG] WORKSPACE_CHECK", JSON.stringify({
      workspaceId: workspaceId || null,
      profile,
      weekDayKeysCount: weekDayKeys.length
    }));
    
    if (!workspaceId) {
      console.warn("[SAVE] No workspaceId available");
      console.error("[AUTO_SAVE_DBG] ABORTED_NO_WORKSPACE", JSON.stringify({
        profile,
        reason: "no-workspaceId"
      }));
      return;
    }

    // Crear workspace si no existe
    try {
      await createWorkspaceIfMissing(workspaceId, db);
    } catch (err) {
      console.error("[SAVE] Error creating workspace:", err);
      console.error("[AUTO_SAVE_DBG] ERROR_CREATE_WORKSPACE", JSON.stringify({
        profile,
        workspaceId,
        error: err?.message || String(err)
      }));
      return;
    }

    // ✅ Coordinación (weekOfChange): cada dateKey pasa por enqueueDocWrite
    // usando la MISMA clave (makeEditLockKey) que las transacciones de
    // edición en dogDayManagerActions.js. Dentro de la tarea, justo cuando
    // realmente va a ejecutarse: se revalida el lock, se relee daysListRef
    // (nunca el `daysList` cerrado 300ms antes al disparar el debounce) y
    // se actualiza lastSavedDaysListRef con el array REALMENTE escrito.
    const savePromises = weekDayKeys.map((dateKey) => {
      const lockKey = makeEditLockKey(workspaceId, profile, dateKey);
      return enqueueDocWrite(lockKey, async () => {
        // Revalidar lock justo antes de ejecutar: si existe un lock
        // pending/committed/failed para este documento, omitir saveDogs
        // (la coordinación de esa escritura corresponde a la transacción
        // de edición, no al autoguardado).
        const activeLock = editLocksRef.current.get(lockKey);
        if (activeLock && (activeLock.status === 'pending' || activeLock.status === 'committed' || activeLock.status === 'failed')) {
          console.error("[AUTO_SAVE_DBG] SKIP_LOCK_ACTIVE", JSON.stringify({
            dateKey,
            lockKey,
            status: activeLock.status,
            timestamp: new Date().toISOString()
          }));
          return;
        }

        // Releer perros desde daysListRef.current (versión más reciente),
        // NO desde el `daysList` capturado en el closure del effect.
        const currentDaysList = daysListRef.current || {};
        const compositeKey = `${profile}::${dateKey}`;

        // ✅ PASO 1: Verificar si el día EXISTE en state (composite key presente)
        const existsInState = currentDaysList && Object.prototype.hasOwnProperty.call(currentDaysList, compositeKey);

        if (!existsInState) {
          // ❌ NO EXISTE EN STATE → SALTARLO
          console.error("[AUTO_SAVE_DBG] UNDEFINED_SKIP", JSON.stringify({
            dateKey,
            compositeKey,
            profile,
            reason: "key-not-found-in-daysList",
            hasDaysList: !!currentDaysList,
            timestamp: new Date().toISOString()
          }));
          return;
        }

        // ✅ PASO 2: Existe en state, obtener dogs (normalización idéntica a la anterior)
        const dogsForDay = getDogsFor(currentDaysList, profile, dateKey);
        const dogs = localDogObjects(dogsForDay || []);

        // [AUTOSAVE_TASK_READ]: instrumentación temporal — tarea de
        // autoguardado iniciada para este dateKey; orden EXACTO leído de
        // daysListRef.current en este instante (justo antes de saveDogs).
        console.log('[AUTOSAVE_TASK_READ]', JSON.stringify({
          profile,
          dayKey: dateKey,
          compositeKey,
          order: dogs.map(d => ({ identity: getDogIdentity(d), originalOrder: d.originalOrder })),
          timestamp: Date.now()
        }));


        // ✅ PASO 3: Decidir si persistir
        if (dogs.length === 0) {
          // Vacío - SOLO persistir si el usuario lo marcó como deletedAll explícitamente
          if (deletedAllKeysRef.current.has(compositeKey)) {
            // ✅ USUARIO BORRÓ EXPLÍCITAMENTE TODOS → PERSISTIR VACÍO
            console.error("[AUTO_SAVE_DBG] EMPTY_PERSIST_DELETED_ALL", JSON.stringify({
              dateKey,
              compositeKey,
              profile,
              path: `workspaces/${workspaceId}/days/${dateKey}/profiles/${profile}`,
              dogsLen: 0,
              deletedAll: true,
              reason: "user-explicitly-deleted-all-dogs",
              timestamp: new Date().toISOString()
            }));
          } else {
            // ❌ PLACEHOLDER VACÍO (no marcado deletedAll) → SKIPEAR
            console.error("[AUTO_SAVE_DBG] SKIP_EMPTY_NOT_DELETED_ALL", JSON.stringify({
              dateKey,
              compositeKey,
              profile,
              dogsLen: 0,
              inDeletedAll: false,
              reason: "empty-placeholder-not-deleted",
              timestamp: new Date().toISOString()
            }));
            return; // SKIP - no persistir vacío
          }
        } else {
          // Normal: persistir con perros
          console.error("[AUTO_SAVE_DBG] NORMAL_PERSIST", JSON.stringify({
            dateKey,
            compositeKey,
            profile,
            dogsLen: dogs.length,
            timestamp: new Date().toISOString()
          }));
        }

        console.error("[SAVE_DOGS_CALL]", JSON.stringify({
          workspaceId,
          dayKey: dateKey,
          profileName: profile,
          dogsCount: dogs.length,
          compositeKey,
          timestamp: new Date().toISOString()
        }));

        const traceDogsAutosave = dogs.filter(d => d?.name === 'POMPON' || d?.name === 'MUSHU' || d?.name === 'PIPPO' || d?.name === 'MURA' || d?.name === 'RIO' || d?.name === 'CLAY' || d?.name === 'MOWGLY' || d?.name === 'LARA');


        traceDogsAutosave.forEach(traceDogAutosave => {
          console.log('[NOTE_TRACE]', JSON.stringify({
            source: 'AUTOSAVE_WRITE',
            dogId: traceDogAutosave.id || null,
            name: traceDogAutosave.name,
            dayKey: dateKey,
            note: traceDogAutosave.informativeNote,
            override: traceDogAutosave.informativeNoteOverride,
            ts: Date.now(),
          }));
        });

        try {
          await saveDogs(workspaceId, dateKey, dogs, profile);


          // LOG [AUTO_SAVE_DBG]: Después de guardar éxito
          console.error("[AUTO_SAVE_DBG] SAVE_SUCCESS", JSON.stringify({
            profile,
            workspaceId,
            dayKey: dateKey,
            compositeKey,
            dogsLen: dogs.length,
            targetRef: {
              path: `workspaces/${workspaceId}/days/${dateKey}/profiles/${profile}`
            },
            timestamp: new Date().toISOString()
          }));

          console.log(`[SAVE] ✓ day ${dateKey} saved successfully`);

          // Actualizar lastSavedDaysListRef con el MISMO array realmente
          // escrito (no un dogs capturado antes de entrar en la cola).
          if (lastSavedDaysListRef?.current) {
            lastSavedDaysListRef.current[compositeKey] = dogs;
          }
        } catch (e) {
          console.error(`[SAVE ERROR] for day ${dateKey}:`, e);
          console.error("[AUTO_SAVE_DBG] SAVE_ERROR", JSON.stringify({
            profile,
            workspaceId,
            dayKey: dateKey,
            dogsLen: dogs.length,
            error: e?.message || String(e),
            targetRef: {
              path: `workspaces/${workspaceId}/days/${dateKey}/profiles/${profile}`
            },
            timestamp: new Date().toISOString()
          }));
          throw e;
        }
      });
    });

    try {
      await Promise.all(savePromises);

      console.log("[SAVE] ✓ all days processed and saved successfully");
      console.error("[AUTO_SAVE_DBG] ALL_DAYS_SAVED", JSON.stringify({
        profile,
        workspaceId,
        weekDayKeysCount: weekDayKeys.length,
        weekDayKeys: weekDayKeys,
        timestamp: new Date().toISOString()
      }));
    } catch (e) {
      console.error("[SAVE ERROR]", e);
      console.error("[AUTO_SAVE_DBG] BATCH_SAVE_FAILED", JSON.stringify({
        profile,
        workspaceId,
        weekDayKeysCount: weekDayKeys.length,
        error: e?.message || String(e),
        timestamp: new Date().toISOString()
      }));
    }
  }, 300); // debounce 300ms para evitar guardados excesivos


    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysListKey, manager.currentDate, manager.selectedUser]);

  // -------------------------------------------------
  // GUARDADO ESTADOS: sube completedDogs y urgentDogs a Firestore
  // -------------------------------------------------
  const completedDogsKey = JSON.stringify(manager.completedDogs);
  const urgentDogsKey = JSON.stringify(manager.urgentDogs);
  const statesSaveTimer = useRef(null);

  useEffect(() => {
    if (!manager.currentDate || !manager.selectedDayKey) return;

    // MODO LOCAL: memory only, no persistence
    if (LOCAL_ONLY_MODE) {
      console.log("[LOCAL_TEST] LOCAL_ONLY_MODE active - NOT saving states anywhere");
      return;
    }

    // Skip if no states to save
    const hasAnyStates = Object.keys(manager.completedDogs || {}).length > 0 || 
                        Object.keys(manager.urgentDogs || {}).length > 0;

    if (!hasAnyStates) {
      console.log("[SAVE_STATES] skipped: no states to save");
      return;
    }

     clearTimeout(statesSaveTimer.current);
     statesSaveTimer.current = setTimeout(async () => {
       // Obtener todos los días de la semana actual
       const weekDays = getWeekDays(manager.currentDate);
       const weekDayKeys = weekDays.map(date => formatDateKey(date));
       
       // LOG de generación de dayKeys/weekKeys para guardado de estados
       console.info('[DAYKEY_GENERATION]', {
         tzOffset: new Date().getTimezoneOffset(),
         isoStringNow: new Date().toISOString(),
         weekKey: `W${weekDays[0]?.toISOString().split('T')[0]}`,
         weekDayKeys: weekDayKeys,
         from: 'AppCore:setTimeout[saveStates]'
       });
       
       console.log("[SAVE_STATES] saving week states:", weekDayKeys);
       
       // Guardar estados para cada día de la semana del perfil actual
       if (!workspaceId) {
        console.warn("[SAVE_STATES] No workspaceId available");
        return;
      }

      // Guardar estados para cada día de la semana del perfil actual
      const savePromises = weekDayKeys.map(dayKey => {
        // Filtrar solo las claves que corresponden a este día
        const dayCompleted = Object.fromEntries(
          Object.entries(manager.completedDogs || {}).filter(([k]) => k.startsWith(`${dayKey}-`))
        );
        const dayUrgent = Object.fromEntries(
          Object.entries(manager.urgentDogs || {}).filter(([k]) => k.startsWith(`${dayKey}-`))
        );

        // 🔒 No crear ni recrear el doc de perfil si este día no tiene estados propios
        if (Object.keys(dayCompleted).length === 0 && Object.keys(dayUrgent).length === 0) {
          return Promise.resolve();
        }

        console.info('[UID_CALL]', { 
          fn: 'saveStates', 
          workspaceId: workspaceId, 
          dayKey,
          visibleProfile: manager.selectedUser 
        });
        console.log(`[SAVE_STATES] saving states to ${dayKey}:`, {
          completed: Object.keys(dayCompleted).length,
          urgent: Object.keys(dayUrgent).length
        });
        return saveStates(workspaceId, dayKey, dayCompleted, dayUrgent, manager.selectedUser);

      });

      try {
        await Promise.all(savePromises);
        console.log("[SAVE_STATES] ✓ all states saved successfully");
      } catch (e) {
        console.error("[SAVE_STATES ERROR]", e);
      }
    }, 300); // debounce 300ms

    return () => clearTimeout(statesSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedDogsKey, urgentDogsKey, manager.selectedDayKey]);

  // -------------------------------------------------
  // GUARDADO ROUTE STATUS: sube routeStatus GLOBAL a Firestore
  // Se guarda en el workspace root, independiente del día
  // -------------------------------------------------
  const routeStatusSaveTimer = useRef(null);
  const routeStatusKey = JSON.stringify(routeStatus);
  const prevSelectedUserRef = useRef(null);

  useEffect(() => {
    if (!manager.selectedUser) return;
    
    // ✅ CRÍTICO: No guardar hasta que Firestore haya hidratado routeStatus
    if (!routeStatusHydrated.current) {
      console.log("[SAVE_ROUTE_STATUS] skipped: not hydrated yet - esperando datos de Firestore");
      return;
    }

    // 🔒 PREVENCIÓN BUG: No guardar si el perfil seleccionado acaba de cambiar
    // Dar tiempo a que se sincronice el nuevo routeStatus desde la nube
    if (prevSelectedUserRef.current !== manager.selectedUser) {
      console.log("[SAVE_ROUTE_STATUS] skipped: profile changed - waiting for cloud sync");
      prevSelectedUserRef.current = manager.selectedUser;
      return;
    }

     clearTimeout(routeStatusSaveTimer.current);
     routeStatusSaveTimer.current = setTimeout(async () => {
       if (!workspaceId) {
        console.warn("[SAVE_ROUTE_STATUS] No workspaceId available");
        return;
      }

      // Guardar el estado del perfil seleccionado actual
      const statusToSave = routeStatus[manager.selectedUser];
      console.log("[SAVE_ROUTE_STATUS] saving for profile:", manager.selectedUser, "status:", statusToSave);

      try {
        await saveRouteStatus(workspaceId, manager.selectedUser, statusToSave);
        console.log("[SAVE_ROUTE_STATUS] ✓ saved successfully");
      } catch (e) {
        console.error("[SAVE_ROUTE_STATUS ERROR]", e);
      }
    }, 300); // debounce 300ms

    return () => clearTimeout(routeStatusSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStatusKey, manager.selectedUser]);

  // ✅ NUEVO: Wrapper que ejecuta extendTemplateWindow después de importar el PDF.
  // Orden: 1) limpiar estados  2) importar PDF (en handleBulkUpdateFromPDF)  3) extender plantilla
  // El usuario carga el PDF y automáticamente se replican los perros fijos a 26 semanas.
   const handleBulkUpdateWithExtend = React.useCallback(async (pdfText) => {
     await manager.handleBulkUpdateFromPDF(pdfText);   // 1+2: limpiar + importar PDF
     if (workspaceId && manager.selectedUser) {
      extendTemplateWindow(workspaceId, manager.selectedUser)   // 3: extender plantilla
        .then(({ extended, skipped }) => {
          if (extended > 0)
            console.log(`[TEMPLATE_EXTEND] ✅ Extended ${extended} days, skipped ${skipped}`);
        })
        .catch(err => console.error('[TEMPLATE_EXTEND_ERROR]', err));
    }
  }, [manager.selectedUser, manager.handleBulkUpdateFromPDF]);

  // ✅ Wrapper para carga PDF: si devuelve false (bloqueado en Free), muestra aviso con acción
  const handlePdfUpload = React.useCallback(async (pdfText) => {
    // Mostrar feedback de progreso al usuario
    const loadingToast = toast({
      title: "Actualizando lista…",
      description: "No cierres la app",
      duration: Infinity,
    });

    try {
      const result = await manager.handleBulkUpdateFromPDF(pdfText);
      loadingToast.dismiss();

      if (result === false) {
        const _pdfToastHolder = {};
        const _pdfToast = toast({
          title: "PDF no cargado",
          description: "El plan Free permite hasta 3 clientes por día. Puedes actualizar a Premium para cargar este PDF.",
          duration: Infinity,
          className: "border-2 border-blue-500",
          action: (
            <Button
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-600 text-white shrink-0"
              onClick={() => { _pdfToastHolder.dismiss?.(); setIsPlanUpgradeOpen(true); }}
            >
              Ver planes
            </Button>
          ),
        });
        _pdfToastHolder.dismiss = _pdfToast.dismiss;
      } else {
        toast({
          title: "Lista actualizada",
          description: "Los clientes se han cargado correctamente.",
        });
      }
    } catch (error) {
      loadingToast.dismiss();
      toast({
        title: "Error al cargar el PDF",
        description: "No se pudo procesar el archivo. Intenta de nuevo.",
        variant: "destructive",
      });
    }
  }, [manager.handleBulkUpdateFromPDF, toast, setIsPlanUpgradeOpen]);

   // ✅ Fase 6B: Bloqueo de límite de clientes en Free (máximo 3 por día)
    const guardedHandleAddDog = useCallback(
     (...args) => {
       if (!permissions.canUseUnlimitedClients && manager.currentDogList.length >= 3) {
         const _addDogToastHolder = {};
         const _addDogToast = toast({
           title: "Límite del plan Free",
           description: "El plan Free permite hasta 3 clientes por día. Puedes actualizar a Premium para añadir más.",
           duration: Infinity,
           className: "border-2 border-blue-500",
           action: (
             <Button
               size="sm"
               className="bg-yellow-500 hover:bg-yellow-600 text-white shrink-0"
               onClick={() => { _addDogToastHolder.dismiss?.(); setIsPlanUpgradeOpen(true); }}
             >
               Ver planes
             </Button>
           ),
         });
         _addDogToastHolder.dismiss = _addDogToast.dismiss;
         return;
       }
       return manager.handleAddDogToList(...args);
     },
     [manager.handleAddDogToList, manager.currentDogList.length, permissions.canUseUnlimitedClients, toast, setIsPlanUpgradeOpen]
   );

  const openAddDogDialog = () => {
    const t = document.getElementById("add-dog-dialog-trigger");
    if (t) t.click();
  };
  const openPdfUploadDialog = () => setIsPdfUploadOpen(true);

  // Funciones para navegar por semanas
  const handlePreviousWeek = () => {
    const newDate = new Date(manager.currentDate);
    newDate.setDate(manager.currentDate.getDate() - 7);
    manager.handleDateChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(manager.currentDate);
    newDate.setDate(manager.currentDate.getDate() + 7);
    manager.handleDateChange(newDate);
  };

   // ✅ CANDADO DE REORDENACIÓN: Reset cuando cambia el perfil activo
   useEffect(() => {
     setReorderUnlocked(false);
   }, [manager.selectedUser]);

   // Manejador para abrir el modal en modo "crear aviso nuevo" (broadcast).
   const handleOpenAlertModal = () => {
     setAlertModalMode('new');
     setIsAlertModalOpen(true);
   };

  // Manejador para abrir el modal en modo "responder al aviso mostrado"
  // (dirigido, vía sendAlertReply). Usado exclusivamente desde el botón
  // "Responder" del AlertBanner sobre currentAlert.
  const handleOpenReplyModal = () => {
    setAlertModalMode('reply');
    setIsAlertModalOpen(true);
  };

  // Manejador para el botón de avisos en el header (con validación de permisos)
  const handleAlertButtonClick = () => {
    if (!permissions.canUseTeamAlerts) {
      toast({
        title: "Función Premium Pro",
        description: "Los avisos entre perfiles están disponibles en el plan Premium Pro.",
        variant: "destructive",
      });
      return;
    }
    setAlertModalMode('new');
    setIsAlertModalOpen(true);
  };

  // Manejador para limpiar el perfil activo únicamente (borrado scoped)
  const handleClearAllProfiles = async () => {
    // Guard: evitar doble-ejecución si se pulsa el botón más de una vez
    if (isClearingProfileRef.current) return;
    isClearingProfileRef.current = true;

     const selectedUser = manager.selectedUser;
     const currentDaysList = manager.daysList || {};

     // Mostrar feedback de progreso al usuario
    const clearingToast = toast({
      title: "Borrando contenido del perfil…",
      description: "No cierres la app",
      duration: Infinity,
    });

    // 1. Filtrar composite keys del perfil activo
    const profileCompositeKeys = Object.keys(currentDaysList)
      .filter(k => k.startsWith(`${selectedUser}::`));
    
    // Extraer dateKeys de memoria (solo semana visible)
    const inMemoryDateKeys = profileCompositeKeys.map(k => 
      k.replace(`${selectedUser}::`, '')
    );

    // Proyectar los 5 días de la semana actual × 56 semanas hacia adelante (~13 meses)
    // para cubrir propagaciones de hasta 52 semanas (PDF inicial + extendTemplateWindow)
    // con margen de seguridad adicional
    const allDateKeysSet = new Set(inMemoryDateKeys);
    const weekDays = getWeekDays(manager.currentDate);
    weekDays.map(d => formatDateKey(d)).forEach(baseDayKey => {
      for (let i = 0; i <= 56; i++) {
        allDateKeysSet.add(addWeeksToDateKey(baseDayKey, i));
      }
    });

    // Retroactivo: cubrir las últimas 13 semanas hacia atrás desde la fecha actual
    // Incluye semanas pasadas con datos residuales recientes.
    weekDays.map(d => formatDateKey(d)).forEach(baseDayKey => {
      for (let j = 1; j <= 13; j++) {
        const pastDate = new Date(parseDateKey(baseDayKey));
        pastDate.setDate(pastDate.getDate() - j * 7);
        allDateKeysSet.add(formatDateKey(pastDate));
      }
    });
    const dateKeysToClean = Array.from(allDateKeysSet);

    // 2. Limpiar Firestore por lotes (dogs + states + routeStatus, 15 dateKeys por lote)
    // Evita saturar IndexedDB y la cola de escrituras de Firestore en Android
    const BATCH_SIZE = 15;
    if (workspaceId && dateKeysToClean.length > 0) {
      try {
        for (let i = 0; i < dateKeysToClean.length; i += BATCH_SIZE) {
          const batch = dateKeysToClean.slice(i, i + BATCH_SIZE);
          await Promise.all([
            ...batch.map(dateKey => saveDogs(workspaceId, dateKey, [], selectedUser)),
            ...batch.map(dateKey => saveStates(workspaceId, dateKey, {}, {}, selectedUser)),
            ...batch.map(dateKey => saveRouteStatus(workspaceId, dateKey, { [selectedUser]: null })),
          ]);
        }
      } catch (error) {
        console.error("[CLEAR_ALL_ERROR]", error);
        clearingToast.dismiss();
        toast({
          title: "Error",
          description: "No se pudo borrar los perfiles. Intenta de nuevo.",
          variant: "destructive",
        });
        isClearingProfileRef.current = false;
        return;
      }
    }

    // 3. Limpiar estado local SOLO para el perfil activo (sin tocar otros perfiles)
    // daysList: eliminar composite keys del perfil activo
    setDaysList(prevDaysList => {
      const newDaysList = { ...prevDaysList };
      profileCompositeKeys.forEach(k => delete newDaysList[k]);
      return newDaysList;
    });

    // 3b. Reset COMPLETO de completedDogs y urgentDogs (incluye días pasados)
    setCompletedDogs({});
    setUrgentDogs({});

    // 3c. Limpiar routeStatus del perfil activo localmente
    setRouteStatus(prev => {
      const next = { ...prev };
      delete next[selectedUser];
      return next;
    });

    // 4. Limpiar localStorage solo para el perfil activo
    if (selectedUser) {
      saveToStorage(LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS, [], selectedUser);
    }

    clearingToast.dismiss();
    toast({
      title: "Lista limpiada",
      description: "Se han eliminado todos los clientes del perfil activo.",
    });
    isClearingProfileRef.current = false;
  };

  // Manejador para enviar un aviso NUEVO (broadcast) o una RESPUESTA
  // dirigida al aviso actualmente mostrado, según alertModalMode.
  // - 'new': saveAlert (broadcast), comportamiento sin cambios.
  // - 'reply': sendAlertReply (dirigido al creador del currentAlert),
  //   NUNCA escribe en settings/alerts.
  const handleSubmitAlert = async (message) => {
    if (!workspaceId) {
      toast({
        title: "Error",
        description: "No se pudo obtener el workspace ID",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingAlert(true);

    try {
      if (alertModalMode === 'reply') {
        // Capturar destinatario y referencia ANTES de escribir, del aviso
        // original vigente (currentAlert). Nunca derivar de otra fuente.
        const recipientProfileId = currentAlert?.createdByProfileId;
        const originAlertId = currentAlert?.alertId;

        if (!recipientProfileId || !originAlertId) {
          throw new Error('No hay aviso original vigente al que responder');
        }

        await sendAlertReply(workspaceId, {
          type: 'reply',
          message,
          senderProfileId: alertsProfileId,
          senderProfileName: alertsProfileName,
          recipientProfileId,
          originAlertId,
        });

        // Orden obligatorio: 1) reply guardada con éxito (arriba) 2) dismiss
        // del aviso original para el perfil que respondió 3) toast/cierre.
        // Solo se ejecuta si sendAlertReply no lanzó excepción. Si handleDismiss
        // fallara, cae en el mismo catch general de este método (igual que
        // el comportamiento ya existente en handleConfirmAlert).
        await handleDismiss();

        toast({
          title: "Respuesta enviada",
          duration: 2000,
          className: "bg-green-50 border-green-300 text-green-900",
        });
        setIsAlertModalOpen(false);
      } else {
        // 1. Guardar aviso en la colección de alertas (broadcast, sin cambios)
        await saveAlert(workspaceId, message, selectedUserName, manager.selectedUser);

        // 2. Crear push event para disparar notificaciones a otros miembros
        try {
          const senderDeviceId = getOrCreateDeviceId();
          await createAlertPushEvent(workspaceId, message, senderDeviceId, manager.selectedUser);
          console.log('[ALERT_SUBMIT] Push event created for alert from device:', senderDeviceId);
        } catch (pushError) {
          // No fallar si el push event falla - el aviso ya se creó
          console.error('[ALERT_SUBMIT] Error creating push event:', pushError);
        }

        toast({
          title: "Aviso enviado",
          duration: 2000,
          className: "bg-green-50 border-green-300 text-green-900",
        });
        setIsAlertModalOpen(false);
      }
    } catch (error) {
      console.error('[ALERT_SUBMIT_ERROR]', error);
      toast({
        title: "Error",
        description: alertModalMode === 'reply'
          ? "No se pudo enviar la respuesta. Intenta de nuevo."
          : "No se pudo enviar el aviso. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingAlert(false);
    }
  };

  // Manejador para OK (confirmar lectura) de un aviso. Crea una reacción
  // dirigida al creador del aviso original (sendAlertReply), NUNCA escribe
  // en settings/alerts. Mantiene el dismiss local del aviso para el perfil
  // que pulsó OK (comportamiento actual sin cambios).
  const handleConfirmAlert = async () => {
    if (!workspaceId) {
      console.error('[CONFIRM_ALERT] No workspaceId available');
      return;
    }

    // Capturar destinatario y referencia ANTES de escribir, del aviso
    // original vigente (currentAlert).
    const recipientProfileId = currentAlert?.createdByProfileId;
    const originAlertId = currentAlert?.alertId;

    if (!recipientProfileId || !originAlertId) {
      console.error('[CONFIRM_ALERT] No hay aviso original vigente para confirmar');
      return;
    }

    try {
      const confirmationMessage = `${alertsProfileName} confirma la lectura del aviso`;
      await sendAlertReply(workspaceId, {
        type: 'ack',
        message: confirmationMessage,
        senderProfileId: alertsProfileId,
        senderProfileName: alertsProfileName,
        recipientProfileId,
        originAlertId,
      });
      await handleDismiss();
      console.log('[CONFIRM_ALERT] Alert confirmation sent:', confirmationMessage);
    } catch (error) {
      console.error('[CONFIRM_ALERT] Error:', error);
    }
  };

  // Filtrar: no mostrar el aviso al propio emisor
  const isSelfAlert = !!(currentAlert?.createdByProfileId &&
    currentAlert.createdByProfileId === manager.selectedUser);

   // Determinar si el perfil actual está bloqueado
   const isCurrentProfileLocked = isProfileLocked(manager.selectedUser);

   // Obtener perfil actual para abrir PinDialog desde overlay bloqueado
   const currentProfile = config.profiles?.find(p => p.id === manager.selectedUser) ||
     config.profiles?.find(p => (p.name || '').trim().toLowerCase() === (manager.selectedUser || '').trim().toLowerCase());

   return (
     <>
       {/* AlertBanner - Overlay popup fixed sobre todo */}
       {(config.shareFleetAlerts ?? true) && permissions.canUseTeamAlerts && showAlert && !isSelfAlert && (
         <AlertBanner
           alert={currentAlert}
           onDismiss={handleDismiss}
           onConfirm={handleConfirmAlert}
           onReply={handleOpenReplyModal}
         />
       )}

       {/* AlertBanner de reacción dirigida (OK/respuesta) — SOLO visible para
           el perfil creador del aviso original (recipientProfileId), nunca
           para otros perfiles. Independiente del banner del aviso original. */}
       {(config.shareFleetAlerts ?? true) && permissions.canUseTeamAlerts && currentReply && (
         <AlertBanner
           alert={{
             message: currentReply.type === 'ack'
               ? currentReply.message
               : `${currentReply.senderProfileName || 'Equipo'}: ${currentReply.message}`,
             createdByProfile: currentReply.senderProfileName,
           }}
           onDismiss={handleDismissReply}
         />
       )}

       <PageHeaderWrapper
         currentDate={manager.currentDate}
         currentDateDisplay={getFormattedDateHeader(manager.currentDate)}
         onDateChange={manager.handleDateChange}
         selectedUser={manager.selectedUser}
         onUserChange={manager.handleUserChange}
         routeStatus={routeStatus}
         onRouteStatusChange={handleRouteStatusChange}
         colorCodeEnabled={config?.colorCodeEnabled ?? true}
         statusLabels={config?.statusLabels}
         userRole={userRole}
         requireConfirmation={!!(
           (
             config.profiles?.find(p => p.id === manager.selectedUser) ||
             config.profiles?.find(p => (p.name || '').trim().toLowerCase() === (manager.selectedUser || '').trim().toLowerCase())
           )?.requireConfirmationWhenMarking
         )}
         registerProfilePinDialogOpener={(opener) => {
           profilePinDialogOpenerRef.current = opener;
         }}
         onAlertClick={handleAlertButtonClick}
        showAlertButton={config.shareFleetAlerts && (userRole === 'worker' || config.profiles.length >= 2)}
       />

      {/* Overlay de bloqueo: envuelve la zona operativa */}
      <div className="relative">
        {trustedDeviceChecking && !isCurrentProfileLocked && (
          <div
            className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center gap-4 rounded pointer-events-auto"
            style={{ pointerEvents: 'auto' }}
          >
            <div className="flex flex-col items-center gap-3 text-center">
              <h2 className="text-lg font-bold text-white">Validando dispositivo…</h2>
            </div>
          </div>
        )}

        {isCurrentProfileLocked && !trustedDeviceChecking && (
          <div
            className="absolute inset-0 z-50 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-start pt-12 sm:pt-16 gap-4 rounded pointer-events-auto cursor-pointer"
            style={{ pointerEvents: 'auto' }}
            onClick={() => {
              if (currentProfile) {
                profilePinDialogOpenerRef.current?.(currentProfile);
              }
            }}
          >
            <div className="flex flex-col items-center gap-3 text-center">
              <Lock className="h-12 w-12 text-yellow-600" />
              <div>
                <h2 className="text-lg font-bold text-white">Perfil Bloqueado</h2>
                <p className="text-sm text-gray-100 mt-1">
                  Este perfil está protegido. Desbloquéalo desde la selección de perfiles.
                </p>
              </div>
            </div>
          </div>
        )}

        <DateDisplay
          currentDate={manager.currentDate}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          {...((isCurrentProfileLocked || trustedDeviceChecking) && { style: { pointerEvents: 'none' } })}
        />

        <div className="mb-3">
      <ControlsSectionWrapper
        currentDate={manager.currentDate}
        onDateChange={manager.handleDateChange}
        calculateDailyDogCount={manager.calculateDailyDogCount}
        onClearAll={() => {
          lastLocalStateChangeRef.current = Date.now();
          manager.handleClearAll();
        }}
        onCheckAll={() => {
          lastLocalStateChangeRef.current = Date.now();
          manager.handleCheckAll();
        }}
        selectedDayKey={manager.selectedDayKey}
        daysList={manager.daysList}
        onDaySelect={handleDaySelect}
        onUndo={manager.handleUndo}
        onRedo={manager.handleRedo}
        canUndo={manager.canUndo}
        canRedo={manager.canRedo}
        onPdfUpload={openPdfUploadDialog}
        onClearAllProfiles={handleClearAllProfiles}
        dayDogs={manager.currentDogList.filter(
          // Fase 2: clave de estado por identidad estable (id con fallback
          // legacy por nombre), para no perder la urgencia de clientes con id.
          dog => !manager.urgentDogs[makeDogStateKey(manager.selectedDayKey, dog)]
        )}
        completedDogs={manager.completedDogs}
        requireConfirmation={!!(
          (
            config.profiles?.find(p => p.id === manager.selectedUser) ||
            config.profiles?.find(p => (p.name || '').trim().toLowerCase() === (manager.selectedUser || '').trim().toLowerCase())
          )?.requireConfirmationWhenMarking
        )}
        selectedUser={manager.selectedUser}
        profiles={config.profiles}
        isReadOnly={isSelectedProfileInformational}
        reorderUnlocked={reorderUnlocked}
        setReorderUnlocked={setReorderUnlocked}
      />
      </div>

      <AddDogDialogWrapper
        onAddDog={guardedHandleAddDog}
        currentDate={manager.currentDate}
        triggerId="add-dog-dialog-trigger"
      />

      <PdfUploadDialog
        isOpen={isPdfUploadOpen}
        onOpenChange={setIsPdfUploadOpen}
        onBulkUpdate={handlePdfUpload}
      />

      <DogManagementSectionWrapper
        currentDogList={manager.currentDogList}
        selectedDayKey={manager.selectedDayKey}
        completedDogs={manager.completedDogs}
        urgentDogs={manager.urgentDogs}
        daysList={manager.daysList}
        isReadOnly={isSelectedProfileInformational}
        onDogClick={(dogName, dogId) => {
          lastLocalStateChangeRef.current = Date.now();
          // Fase 2: reenviar identidad estable (dogId) si DogListItem la provee.
          manager.handleDogClick(dogName, dogId);
        }}
        onUrgentClick={(dogName, dogId) => {
          lastLocalStateChangeRef.current = Date.now();
          // Fase 2: reenviar identidad estable (dogId) si DogListItem la provee.
          manager.handleUrgentClick(dogName, dogId);
        }}
        onDelete={manager.handleDeleteDog}
        onEdit={manager.handleEditDogInList}
        onReorder={manager.handleReorderDogs}
        currentDate={manager.currentDate}
        getInitialDayData={manager.getInitialDayData}
        requireConfirmation={!!(
          (
            config.profiles?.find(p => p.id === manager.selectedUser) ||
            config.profiles?.find(p => (p.name || '').trim().toLowerCase() === (manager.selectedUser || '').trim().toLowerCase())
          )?.requireConfirmationWhenMarking
        )}
        onApplyCanceledDaysBatch={manager.handleApplyCanceledDaysBatch}
        reorderUnlocked={reorderUnlocked}
        setReorderUnlocked={setReorderUnlocked}
        isSelectedProfileInformational={isSelectedProfileInformational}
      />
      <ProfileActionBar 
        profiles={config.profiles} 
        selectedUser={manager.selectedUser} 
        onAlertClick={handleOpenAlertModal}
        onOpenDialog={openAddDogDialog}
        shareFleetAlerts={config.shareFleetAlerts ?? true}
      />
      <AlertModal 
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        onSubmit={handleSubmitAlert}
        isLoading={isSubmittingAlert}
      />
      <WelcomeModal onOpenPlanModal={() => setIsPlanUpgradeOpen(true)} />
      <FreeBanner onOpenPlanModal={() => setIsPlanUpgradeOpen(true)} />
      <PlanUpgradeModal 
        isOpen={isPlanUpgradeOpen}
        onClose={() => setIsPlanUpgradeOpen(false)}
      />
      <DebugBar 
        uid={auth.currentUser?.uid} 
        profile={manager.selectedUser} 
        dayKey={manager.selectedDayKey} 
        subActive={unsubscribeMap.current.size > 0} 
      />
      </div>
    </>
  );
}
